
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { SupabaseService } from '../../services/supabase.service';
import { AppUser } from '../../models/user';
import { AuthService } from '../../services/auth.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, FormsModule],
})
export class AdminComponent {
  private supabaseService = inject(SupabaseService);
  private authService = inject(AuthService);

  users = signal<AppUser[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);
  
  actionInProgress = signal<string | null>(null); // Store user ID being actioned

  // Invite functionality state
  inviteEmailPrefix = signal('');
  inviteLoading = signal(false);
  inviteSuccess = signal<string | null>(null);
  inviteError = signal<string | null>(null);

  currentUser = this.authService.currentUser;

  constructor() {
    this.loadUsers();
  }

  async loadUsers() {
    this.loading.set(true);
    this.error.set(null);
    try {
      const userList = await this.supabaseService.getUsers();
      // Sort users by creation date, newest first
      userList.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      this.users.set(userList);
    } catch(e: any) {
      this.error.set(e.message || 'Failed to load users.');
      console.error(e);
    } finally {
      this.loading.set(false);
    }
  }

  async handleInviteReviewer() {
    this.inviteSuccess.set(null);
    this.inviteError.set(null);

    const prefix = this.inviteEmailPrefix().trim();
    if (!prefix) {
      this.inviteError.set('Email prefix cannot be empty.');
      return;
    }

    this.inviteLoading.set(true);
    const email = `${prefix}@cordic.com`;

    try {
      await this.supabaseService.inviteReviewer(email);
      this.inviteSuccess.set(`Invitation successfully sent to ${email}. The user will appear in the list below once they sign in.`);
      this.inviteEmailPrefix.set('');
    } catch (e: any) {
      this.inviteError.set(e.message || 'Failed to send invitation.');
      console.error(e);
    } finally {
      this.inviteLoading.set(false);
    }
  }

  async toggleRole(user: AppUser) {
    if (user.id === this.currentUser()?.id) {
        alert("You cannot change your own role.");
        return;
    }
    this.actionInProgress.set(user.id);
    try {
      await this.supabaseService.updateUserRole(user.id, !user.is_reviewer);
      // Reload the user list to show the change
      await this.loadUsers();
    } catch (e: any) {
      alert(`Failed to update role: ${e.message}`);
    } finally {
      this.actionInProgress.set(null);
    }
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }
}
